import { useState } from "react";

// Real branded car photos per tier — sourced from Unsplash (free-license,
// hotlinkable via canonical photo URLs). If the image fails to load, we
// gracefully fall back to the inline SVG art below so the picker never
// shows a broken image icon.
const TIER_PHOTOS = {
  // Toyota Camry (clean white sedan, ¾ view)
  standard: "https://images.unsplash.com/photo-1580273916550-e323be2ae537?w=280&h=160&fit=crop&auto=format&q=70",
  // Toyota Prius (hybrid, side view)
  hybrid: "https://images.unsplash.com/photo-1619767886558-efdc259cde1a?w=280&h=160&fit=crop&auto=format&q=70",
  // Family MPV / minivan
  family_7_seater: "https://images.unsplash.com/photo-1519641471654-76ce0107ad1b?w=280&h=160&fit=crop&auto=format&q=70",
  // Executive black sedan (Mercedes S-Class)
  vip: "https://images.unsplash.com/photo-1618843479313-40f8afb4b4d8?w=280&h=160&fit=crop&auto=format&q=70",
};

/**
 * VehicleTierArt
 * ──────────────
 * Renders a real branded car photo per taxi tier when available (Unsplash
 * CDN), gracefully falling back to a distinct inline SVG silhouette on any
 * image load error so the picker never shows a broken image icon.
 *
 *  - standard         → clean 4-door sedan (Camry-style)
 *  - hybrid           → sedan w/ green leaf badge (Prius-style)
 *  - family_7_seater  → tall MPV/van
 *  - vip              → sleek executive sedan w/ gold trim (S-Class-style)
 */
export default function VehicleTierArt({ tier = "standard", selected = false, size = 68 }) {
  const [imgFailed, setImgFailed] = useState(false);
  const photoUrl = TIER_PHOTOS[tier];

  if (photoUrl && !imgFailed) {
    return (
      <img
        src={photoUrl}
        alt={tier}
        onError={() => setImgFailed(true)}
        loading="lazy"
        className={`object-cover rounded-lg transition-all ${selected ? "scale-105 shadow-[0_4px_12px_rgba(255,200,0,0.35)]" : "opacity-90"}`}
        style={{ width: size + 20, height: (size * 40) / 68 + 10 }}
        data-testid={`vehicle-photo-${tier}`}
      />
    );
  }

  return <VehicleTierSvgArt tier={tier} selected={selected} size={size} />;
}

function VehicleTierSvgArt({ tier = "standard", selected = false, size = 68 }) {
  const bodyGradId = `vg-${tier}-${selected ? "s" : "u"}`;
  const bodyColorFrom = tier === "vip"
    ? (selected ? "#F5D97B" : "#3E3E3E")
    : tier === "hybrid"
      ? (selected ? "#7DE4B4" : "#D1D5DB")
      : (selected ? "#FFF1B0" : "#E5E7EB");
  const bodyColorTo = tier === "vip"
    ? (selected ? "#C9A24C" : "#1A1A1A")
    : tier === "hybrid"
      ? (selected ? "#2FA872" : "#93A3B0")
      : (selected ? "#FFC800" : "#94A3B0");
  const windowFill = tier === "vip" ? "#0A0A0A" : "#111827";
  const stroke = selected ? "#050505" : "rgba(0,0,0,0.45)";

  const commonProps = { width: size, height: (size * 40) / 68, viewBox: "0 0 100 60", xmlns: "http://www.w3.org/2000/svg" };

  if (tier === "family_7_seater") {
    return (
      <svg {...commonProps}>
        <defs>
          <linearGradient id={bodyGradId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={bodyColorFrom} />
            <stop offset="100%" stopColor={bodyColorTo} />
          </linearGradient>
        </defs>
        {/* Van body */}
        <rect x="6" y="14" width="88" height="30" rx="8" fill={`url(#${bodyGradId})`} stroke={stroke} strokeWidth="1.5" />
        {/* Roof stripe */}
        <rect x="10" y="14" width="80" height="4" rx="2" fill="rgba(255,255,255,0.15)" />
        {/* Windows split */}
        <rect x="12" y="20" width="26" height="10" rx="2" fill={windowFill} />
        <rect x="42" y="20" width="18" height="10" rx="2" fill={windowFill} />
        <rect x="64" y="20" width="24" height="10" rx="2" fill={windowFill} />
        {/* Sliding door line */}
        <line x1="40" y1="20" x2="40" y2="44" stroke="rgba(0,0,0,0.35)" strokeWidth="1" />
        {/* Wheels */}
        <circle cx="24" cy="46" r="6.5" fill="#0A0A0A" />
        <circle cx="76" cy="46" r="6.5" fill="#0A0A0A" />
        <circle cx="24" cy="46" r="2.5" fill="#3F3F46" />
        <circle cx="76" cy="46" r="2.5" fill="#3F3F46" />
        {/* Family badge */}
        <g transform="translate(48,7)">
          <rect x="-14" y="-6" width="28" height="10" rx="5" fill={selected ? "#050505" : "rgba(0,0,0,0.4)"} />
          <text x="0" y="1" textAnchor="middle" fontSize="7" fontWeight="900" fill={selected ? "#FFC800" : "#F5F5F5"} fontFamily="system-ui, sans-serif">7-SEAT</text>
        </g>
      </svg>
    );
  }

  if (tier === "vip") {
    return (
      <svg {...commonProps}>
        <defs>
          <linearGradient id={bodyGradId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={bodyColorFrom} />
            <stop offset="100%" stopColor={bodyColorTo} />
          </linearGradient>
        </defs>
        {/* Long executive sedan body */}
        <path d="M 6 40 L 12 22 Q 18 18 30 16 L 70 16 Q 82 18 88 22 L 94 40 Q 94 46 88 46 L 12 46 Q 6 46 6 40 Z"
              fill={`url(#${bodyGradId})`} stroke={stroke} strokeWidth="1.5" />
        {/* Tinted windows */}
        <path d="M 20 22 L 30 18 L 70 18 L 80 22 L 74 32 L 26 32 Z" fill={windowFill} opacity="0.9" />
        {/* Gold accent stripe */}
        <line x1="10" y1="38" x2="90" y2="38" stroke={selected ? "#FFC800" : "#8A6A22"} strokeWidth="1.2" />
        {/* Headlights */}
        <circle cx="14" cy="34" r="1.6" fill="#FFF7C2" />
        <circle cx="86" cy="34" r="1.6" fill="#FF5F5F" />
        {/* Wheels */}
        <circle cx="24" cy="46" r="7" fill="#0A0A0A" />
        <circle cx="76" cy="46" r="7" fill="#0A0A0A" />
        <circle cx="24" cy="46" r="3" fill={selected ? "#C9A24C" : "#525252"} />
        <circle cx="76" cy="46" r="3" fill={selected ? "#C9A24C" : "#525252"} />
        {/* VIP badge */}
        <g transform="translate(50,8)">
          <rect x="-10" y="-6" width="20" height="10" rx="5" fill={selected ? "#050505" : "rgba(0,0,0,0.5)"} />
          <text x="0" y="1" textAnchor="middle" fontSize="7" fontWeight="900" fill="#FFC800" fontFamily="system-ui, sans-serif">VIP</text>
        </g>
      </svg>
    );
  }

  // standard + hybrid share sedan silhouette; hybrid gets a leaf badge
  return (
    <svg {...commonProps}>
      <defs>
        <linearGradient id={bodyGradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={bodyColorFrom} />
          <stop offset="100%" stopColor={bodyColorTo} />
        </linearGradient>
      </defs>
      {/* Sedan body */}
      <path d="M 6 42 L 14 26 Q 22 20 34 18 L 66 18 Q 78 20 86 26 L 94 42 Q 94 46 88 46 L 12 46 Q 6 46 6 42 Z"
            fill={`url(#${bodyGradId})`} stroke={stroke} strokeWidth="1.5" />
      {/* Windows */}
      <path d="M 22 26 L 34 22 L 66 22 L 78 26 L 72 34 L 28 34 Z" fill={windowFill} opacity="0.85" />
      {/* Door line */}
      <line x1="50" y1="26" x2="50" y2="42" stroke="rgba(0,0,0,0.3)" strokeWidth="0.8" />
      {/* Wheels */}
      <circle cx="24" cy="46" r="6.5" fill="#0A0A0A" />
      <circle cx="76" cy="46" r="6.5" fill="#0A0A0A" />
      <circle cx="24" cy="46" r="2.5" fill="#3F3F46" />
      <circle cx="76" cy="46" r="2.5" fill="#3F3F46" />
      {/* Hybrid: leaf badge */}
      {tier === "hybrid" && (
        <g transform="translate(50,10)">
          <circle cx="0" cy="0" r="7" fill="#0F5132" stroke="#22C55E" strokeWidth="1" />
          <path d="M -3 2 Q -3 -3 2 -3 Q 3 0 0 3 Q -2 3 -3 2 Z" fill="#22C55E" />
        </g>
      )}
    </svg>
  );
}
